import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { get } from 'lodash';
import { FEATURE_URL, PROMOTION_IMAGES_URL } from '../config';
import { metricsApi } from '../api/metricsApi';

const FeaturesContext = createContext();

class ErrorWithHTTPStatusCode extends Error {
    constructor(message, status) {
        super(message);
        this.status = status;
    }
}

const fetchFeaturesJson = async () => {
    try {
        const { data, headers } = await axios.get(FEATURE_URL);

        if (!new RegExp('^application/json').test(headers['content-type'])) {
            // When feature file isn't returned the response could be a status code 200
            throw new ErrorWithHTTPStatusCode('Invalid features fetched', 404);
        }

        return data;
    } catch (err) {
        const statusCode = get(err, 'response.status', 500);
        await metricsApi.errorMetric(
            'fetchFeaturesJson',
            err instanceof ErrorWithHTTPStatusCode ? err.status : statusCode,
            err.message
        );
        return null;
    }
};

const defaultFeatures = {
    countdown_timer_enabled: true,
    entry_animation_enabled: true,
    betting_links_enabled: true,
    is_gold_cup: false,
    share_enabled: true,
    promotions: {
        enabled: false,
        prerace: {
            enabled: true,
            eventPath: '/horse-racing',
            img: {
                mobile: `${PROMOTION_IMAGES_URL}/pre-race/mobile/pre-race-mobile-default.png`,
                desktop: `${PROMOTION_IMAGES_URL}/pre-race/desktop/pre-race-desktop-default.png`
            }
        },
        inrunning: {
            enabled: true,
            eventPath: '/horse-racing',
            img: {
                mobile: `${PROMOTION_IMAGES_URL}/in-running/mobile/in-running-mobile-default.png`,
                desktop: `${PROMOTION_IMAGES_URL}/in-running/desktop/in-running-desktop-default.png`
            }
        },
        runner: {
            enabled: true,
            eventPath: '/horse-racing' //  i.e. '/horse-racing/event/2108349'
        }
    }
};

export const FeaturesProvider = ({ children }) => {
    const [features, setFeatures] = useState(defaultFeatures);

    useEffect(() => {
        const fetchingSetFeatures = async () => {
            const data = await fetchFeaturesJson();
            if (data) {
                setFeatures(data);
            }
        };
        fetchingSetFeatures();
    }, []);

    return (
        <FeaturesContext.Provider value={{ features, setFeatures }}>
            {children}
        </FeaturesContext.Provider>
    );
};

FeaturesProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export const useFeatures = () => useContext(FeaturesContext);
